import React, { useState, useEffect } from 'react';
import { Upload, Download, AlertCircle, FileJson, X, CheckCircle, AlertTriangle, Table } from 'lucide-react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const BulkRelationshipManager = ({ tables, onClose, onSuccess }) => {
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState(null);
  const [validationResults, setValidationResults] = useState({ errors: [], warnings: [] });
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Helper function to normalize foreign keys to consistent format with proper field order
  const normalizeForeignKeys = (foreignKeys) => {
    if (!foreignKeys || !Array.isArray(foreignKeys)) return [];
    
    return foreignKeys.map(fk => {
      // Ensure consistent field ordering: columns, references, type
      return {
        columns: fk.columns || [fk.column],
        references: fk.references,
        type: fk.type || 'n:1'
      };
    });
  };

  // Export all relationships
  const handleExportAll = () => {
    const hasAnyRelationships = tables.some(table => 
      table.primaryKey || (table.foreignKeys && table.foreignKeys.length > 0)
    );

    let exportData;

    if (hasAnyRelationships) {
      // Export actual relationships - NORMALIZED with consistent field order
      exportData = {
        tables: {}
      };

      tables.forEach(table => {
        exportData.tables[table.name] = {
          primaryKey: table.primaryKey || null,
          foreignKeys: normalizeForeignKeys(table.foreignKeys)
        };
      });
    } else {
      // Export template with examples
      exportData = {
        tables: {
          "example_orders": {
            primaryKey: "order_id",
            foreignKeys: [
              {
                columns: ["customer_id"],
                references: {
                  table: "customers",
                  columns: ["id"]
                },
                type: "n:1"
              }
            ]
          },
          "example_order_items": {
            primaryKey: ["order_id", "item_id"],
            foreignKeys: [
              {
                columns: ["order_id"],
                references: {
                  table: "orders",
                  columns: ["order_id"]
                },
                type: "n:1"
              },
              {
                columns: ["product_id"],
                references: {
                  table: "products",
                  columns: ["id"]
                },
                type: "n:1"
              }
            ]
          },
          "_instructions": {
            "_note": "Replace example tables with your actual table names",
            "_format": {
              "primaryKey": "string for single column, array for composite, or null",
              "foreignKeys": "array of foreign key definitions",
              "type_options": ["n:1", "1:1", "1:n", "n:n"]
            },
            "_important": "Table names must match exactly with existing tables"
          }
        }
      };
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `all_relationships_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export clean template
  const handleExportTemplate = () => {
    const templateData = {
      tables: {}
    };

    // Add all tables with empty relationships
    tables.forEach(table => {
      templateData.tables[table.name] = {
        primaryKey: null,
        foreignKeys: []
      };
    });

    // Add example entries to help users understand the format
    templateData.tables["_example_orders"] = {
      "_comment": "This is an example - replace with your actual relationships",
      "primaryKey": "order_id",
      "foreignKeys": [
        {
          "columns": ["customer_id"],
          "references": {
            "table": "customers",
            "columns": ["id"]
          },
          "type": "n:1",
          "_comment": "Each order belongs to one customer"
        }
      ]
    };

    templateData.tables["_example_order_items"] = {
      "_comment": "Example with composite primary key and multiple foreign keys",
      "primaryKey": ["order_id", "item_id"],
      "foreignKeys": [
        {
          "columns": ["order_id"],
          "references": {
            "table": "orders",
            "columns": ["order_id"]
          },
          "type": "n:1"
        },
        {
          "columns": ["product_id"],
          "references": {
            "table": "products",
            "columns": ["id"]
          },
          "type": "n:1"
        }
      ]
    };

    templateData.tables["_instructions"] = {
      "_note": "Remove all entries starting with '_' before importing",
      "_format": {
        "primaryKey": "string for single column, array for composite, or null",
        "foreignKeys": "array of foreign key definitions",
        "type_options": ["n:1", "1:1", "1:n", "n:n"]
      },
      "_important": [
        "Table names must match exactly with existing tables",
        "All tables not included will have relationships cleared",
        "Column names must exist in the referenced tables"
      ]
    };

    const blob = new Blob([JSON.stringify(templateData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relationship_template_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Comprehensive validation
  const validateBulkImport = (data) => {
    const errors = [];
    const warnings = [];
    const tableMap = {};
    
    // Create table lookup maps
    tables.forEach(t => {
      tableMap[t.name] = t;
    });

    // Check basic structure
    if (!data.tables || typeof data.tables !== 'object') {
      errors.push("JSON must contain a 'tables' object.");
      return { errors, warnings };
    }

    // Track all referenced tables to check existence
    const referencedTables = new Set();

    // Validate each table's relationships
    Object.entries(data.tables).forEach(([tableName, relationships]) => {
      // Skip instruction entries
      if (tableName.startsWith('_')) return;

      // Check if table exists
      if (!tableMap[tableName]) {
        errors.push(`Table "${tableName}" does not exist in the system.`);
        return;
      }

      const table = tableMap[tableName];

      // Validate structure
      if (!('primaryKey' in relationships) || !('foreignKeys' in relationships)) {
        errors.push(`Table "${tableName}": Must have 'primaryKey' and 'foreignKeys' fields.`);
        return;
      }

      // Validate primary key
      const { primaryKey } = relationships;
      if (primaryKey !== null) {
        if (Array.isArray(primaryKey)) {
          if (primaryKey.length === 0) {
            errors.push(`Table "${tableName}": Composite primary key cannot be empty.`);
          }
          primaryKey.forEach(col => {
            if (!table.schema?.some(field => field.name === col)) {
              errors.push(`Table "${tableName}": Primary key column "${col}" does not exist.`);
            }
          });
          if (new Set(primaryKey).size !== primaryKey.length) {
            errors.push(`Table "${tableName}": Duplicate columns in primary key.`);
          }
        } else if (typeof primaryKey === 'string') {
          if (!table.schema?.some(field => field.name === primaryKey)) {
            errors.push(`Table "${tableName}": Primary key column "${primaryKey}" does not exist.`);
          }
        } else {
          errors.push(`Table "${tableName}": Primary key must be string, array, or null.`);
        }
      }

      // Validate foreign keys
      if (!Array.isArray(relationships.foreignKeys)) {
        errors.push(`Table "${tableName}": foreignKeys must be an array.`);
        return;
      }

      const fkColumns = [];
      relationships.foreignKeys.forEach((fk, index) => {
        // More robust format detection that doesn't depend on field order
        let columns, refTable, refColumns;
        
        // Check for old format
        if ('column' in fk && typeof fk.references === 'string') {
          // Old format: { column: "col", references: "table.column", type: "n:1" }
          columns = [fk.column];
          const refParts = fk.references.split('.');
          refTable = refParts[0];
          refColumns = [refParts.slice(1).join('.')]; // Handle cases where column name has dots
        } 
        // Check for new format
        else if ('columns' in fk && fk.references && typeof fk.references === 'object') {
          // New format: { columns: [...], references: { table: "...", columns: [...] }, type: "n:1" }
          columns = fk.columns;
          refTable = fk.references.table;
          refColumns = fk.references.columns;
        } else {
          errors.push(`Table "${tableName}", FK ${index + 1}: Invalid format. Use either old format (column/references string) or new format (columns array/references object).`);
          return;
        }

        // Validate type
        if (!fk.type) {
          errors.push(`Table "${tableName}", FK ${index + 1}: Missing 'type' field.`);
          return;
        }

        // Validate columns exist in current table
        columns.forEach(col => {
          if (!table.schema?.some(field => field.name === col)) {
            errors.push(`Table "${tableName}": Foreign key column "${col}" does not exist.`);
          }
          fkColumns.push(col);
        });

        // Track referenced table
        referencedTables.add(refTable);

        // Validate type
        const validTypes = ['n:1', '1:1', '1:n', 'n:n'];
        if (!validTypes.includes(fk.type)) {
          errors.push(`Table "${tableName}", FK ${index + 1}: Invalid type "${fk.type}". Must be one of: ${validTypes.join(', ')}`);
        }

        // Column count validation (only for new format - old format is always 1:1)
        if (fk.columns && fk.references && typeof fk.references === 'object') {
          if (columns.length !== refColumns.length) {
            errors.push(`Table "${tableName}", FK ${index + 1}: Column count mismatch.`);
          }
        }
      });

      // Check for duplicate FK columns
      if (new Set(fkColumns).size !== fkColumns.length) {
        warnings.push(`Table "${tableName}": Some columns have multiple foreign keys.`);
      }
    });

    // Validate all referenced tables exist
    referencedTables.forEach(refTable => {
      if (!tableMap[refTable]) {
        errors.push(`Referenced table "${refTable}" does not exist.`);
      } else {
        // Validate referenced columns
        Object.entries(data.tables).forEach(([tableName, relationships]) => {
          if (tableName.startsWith('_')) return;
          
          relationships.foreignKeys?.forEach((fk, index) => {
            let refTableName, refCols;
            
            if (fk.column && typeof fk.references === 'string') {
              // Old format
              const refParts = fk.references.split('.');
              refTableName = refParts[0];
              refCols = [refParts.slice(1).join('.')];
            } else if (fk.references && typeof fk.references === 'object') {
              // New format
              refTableName = fk.references.table;
              refCols = fk.references.columns;
            }
            
            if (refTableName === refTable) {
              const refTableObj = tableMap[refTable];
              refCols.forEach(col => {
                if (!refTableObj.schema?.some(field => field.name === col)) {
                  errors.push(`Table "${tableName}", FK ${index + 1}: Referenced column "${col}" does not exist in "${refTable}".`);
                }
              });
            }
          });
        });
      }
    });

    // Check for main table referencing materialized view or vice versa
    Object.entries(data.tables).forEach(([tableName, relationships]) => {
      if (tableName.startsWith('_')) return;
      const table = tableMap[tableName];
      if (!table) return;

      relationships.foreignKeys?.forEach((fk, index) => {
        let refTableName;
        if (fk.column && typeof fk.references === 'string') {
          refTableName = fk.references.split('.')[0];
        } else if (fk.references && typeof fk.references === 'object') {
          refTableName = fk.references.table;
        }
        
        const refTable = tableMap[refTableName];
        if (refTable && table.isMaterializedView !== refTable.isMaterializedView) {
          errors.push(`Table "${tableName}": Cannot reference ${refTable.isMaterializedView ? 'materialized view' : 'main table'} "${refTableName}" from ${table.isMaterializedView ? 'materialized view' : 'main table'}.`);
        }
      });
    });

    // Add warnings for tables not in import
    tables.forEach(table => {
      if (!data.tables[table.name] && !table.name.startsWith('_')) {
        warnings.push(`Table "${table.name}" not included in import - relationships will be cleared.`);
      }
    });

    return { errors, warnings };
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const { errors, warnings } = validateBulkImport(data);
        
        setValidationResults({ errors, warnings });
        
        if (errors.length === 0) {
          // Clean the data
          const cleanData = {
            tables: {}
          };
          
          Object.entries(data.tables).forEach(([tableName, relationships]) => {
            if (!tableName.startsWith('_')) {
              cleanData.tables[tableName] = {
                primaryKey: relationships.primaryKey,
                foreignKeys: relationships.foreignKeys.map(fk => {
                  // Remove _comment field from each foreign key but keep the foreign key itself
                  const cleanFk = { ...fk };
                  delete cleanFk._comment;
                  return cleanFk;
                })
              };
            }
          });
          
          setImportData(cleanData);
          setShowPreview(true);
        } else {
          setImportData(null);
          setShowPreview(false);
        }
      } catch (error) {
        setValidationResults({ 
          errors: ['Invalid JSON file. Please check the file format.'], 
          warnings: [] 
        });
        setImportData(null);
        setShowPreview(false);
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  // Apply bulk import
  const handleApplyBulkImport = async () => {
    if (!importData) return;

    setSaving(true);
    try {
      const configDoc = doc(db, 'appConfiguration', 'tables');
      const updateData = {};

      // Clear relationships for tables not in import
      tables.forEach(table => {
        if (!importData.tables[table.name]) {
          updateData[`${table.name}.primaryKey`] = null;
          updateData[`${table.name}.foreignKeys`] = [];
        }
      });

      // Apply imported relationships
      Object.entries(importData.tables).forEach(([tableName, relationships]) => {
        updateData[`${tableName}.primaryKey`] = relationships.primaryKey;
        updateData[`${tableName}.foreignKeys`] = relationships.foreignKeys;
      });

      await updateDoc(configDoc, updateData);
      
      onSuccess("All relationships updated successfully!");
      onClose();
    } catch (error) {
      setValidationResults({
        errors: [`Failed to save: ${error.message}`],
        warnings: []
      });
    } finally {
      setSaving(false);
    }
  };

  // Get relationship summary for a table
  const getRelationshipSummary = (tableName, relationships) => {
    const pk = relationships.primaryKey;
    const fkCount = relationships.foreignKeys?.length || 0;
    
    let pkDisplay = 'None';
    if (pk) {
      pkDisplay = Array.isArray(pk) ? `[${pk.join(', ')}]` : pk;
    }
    
    return { pkDisplay, fkCount };
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FileJson className="h-6 w-6 mr-2" />
            Bulk Relationship Management
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-1">Manage All Table Relationships</h3>
              <p className="text-sm text-blue-700">
                Export all current relationships or import a JSON file to update relationships for all tables at once.
                Tables not included in the import will have their relationships cleared.
              </p>
            </div>

            {/* Actions */}
            <div className="flex space-x-4">
              <button
                onClick={handleExportAll}
                className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Current
              </button>
              
              <button
                onClick={handleExportTemplate}
                className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FileJson className="h-4 w-4 mr-2" />
                Download Template
              </button>
              
              <label className="flex-1 flex items-center justify-center px-4 py-2 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary/5 cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Import JSON
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>

            {/* Validation Results */}
            {(validationResults.errors.length > 0 || validationResults.warnings.length > 0) && (
              <div className="space-y-3">
                {validationResults.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-red-800 mb-1">Errors Found</h4>
                        <ul className="text-sm text-red-700 space-y-1">
                          {validationResults.errors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                {validationResults.warnings.length > 0 && validationResults.errors.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-yellow-800 mb-1">Warnings</h4>
                        <ul className="text-sm text-yellow-700 space-y-1">
                          {validationResults.warnings.map((warning, index) => (
                            <li key={index}>• {warning}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Preview */}
            {showPreview && importData && (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    Import Preview - {Object.keys(importData.tables).length} Tables
                  </h3>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Table
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Primary Key
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Foreign Keys
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tables.map(table => {
                        const imported = importData.tables[table.name];
                        const current = getRelationshipSummary(table.name, {
                          primaryKey: table.primaryKey,
                          foreignKeys: table.foreignKeys
                        });
                        const future = imported ? getRelationshipSummary(table.name, imported) : null;
                        
                        return (
                          <tr key={table.name}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {table.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {future ? (
                                <div>
                                  <span className="font-mono">{future.pkDisplay}</span>
                                  {current.pkDisplay !== future.pkDisplay && (
                                    <span className="text-xs text-yellow-600 ml-2">
                                      (was: {current.pkDisplay})
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-red-500">Will be cleared</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {future ? (
                                <div>
                                  <span>{future.fkCount} defined</span>
                                  {current.fkCount !== future.fkCount && (
                                    <span className="text-xs text-yellow-600 ml-2">
                                      (was: {current.fkCount})
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-red-500">Will be cleared</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {future ? (
                                current.pkDisplay === future.pkDisplay && current.fkCount === future.fkCount ? (
                                  <span className="text-gray-500">No change</span>
                                ) : (
                                  <span className="text-blue-600">Will update</span>
                                )
                              ) : (
                                <span className="text-red-600">Will clear</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          {showPreview && (
            <button
              onClick={handleApplyBulkImport}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 disabled:bg-gray-400 flex items-center"
            >
              {saving ? (
                <>
                  <AlertCircle className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Apply All Changes'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkRelationshipManager;